import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import {
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  AlertBannerContext,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import AuthContext from "../../context/authContext";
import { fetchGraphQL } from "../../Graphql/utils";
import { loginQuery } from "../../Graphql/querries/loginQuery";
import {
  LoginRequest,
  LoginResponse
} from "../../components/Login/types/login.types";
import { AUTH_STATUS } from "../../context/enums/authEnums";

import { LOG_LEVEL, buildLogContext, sendAppLog } from "../../Utils/appLogger";
import { trackClarityEvent } from "../../analytics/clarity";

const useLogin = () => {
  const { setAlert } = useContext(AlertBannerContext);
  const { authContextInfo, updateAuthContextInfo } = useContext(AuthContext);

  // ✅ Removed: useNavigate, useLocation, KivuI18nContext, redirectPath logic
  // Navigation is now handled entirely in Login.tsx after authorize succeeds,
  // ensuring the auth cookie is set before any protected route is accessed.

  return useMutation<LoginResponse, Error, LoginRequest>({
    mutationKey: ["login"],

    mutationFn: (input) => fetchGraphQL(loginQuery, { input }, setAlert),

    onSuccess: (response) => {
      const { code, emailVerified, passwordChangeRequired } =
        response?.login || {};

      const accountVerified = emailVerified && !passwordChangeRequired;
      const onlyVerificationRequired =
        !emailVerified && !passwordChangeRequired;
      const onlyPasswordChangeRequired =
        emailVerified && passwordChangeRequired;
      const verificationAndPasswordChangeRequired =
        !emailVerified && passwordChangeRequired;

      if (
        code === API_RESPONSE_CODE.OK ||
        code ===
          API_RESPONSE_CODE.EMAIL_VERIFICATION_AND_PASSWORD_CHANGE_REQUIRED ||
        code === API_RESPONSE_CODE.EMAIL_VERIFICATION_REQUIRED ||
        code === API_RESPONSE_CODE.PASSWORD_CHANGE_REQUIRED
      ) {
        sendAppLog(
          LOG_LEVEL.INFO,
          "Login successful or partially successful",
          buildLogContext(
            "auth",
            "login",
            // userId/companyId/storeId are not yet available — session is not fully established
            undefined,
            undefined,
            undefined,
            {
              apiCode: String(code),
              emailVerified: String(emailVerified),
              passwordChangeRequired: String(passwordChangeRequired)
            }
          )
        );
      }

      if (code === API_RESPONSE_CODE.OK && accountVerified) {
        // ✅ Set AUTHENTICATED — the server has already set the auth cookie
        // as part of this login response. Login.tsx watches for this status,
        // then calls authorize (which now succeeds since cookie is present)
        // and finally navigates. We do NOT navigate here — doing so would
        // trigger AuthProvider's authorize check before the cookie is readable.
        trackClarityEvent("login_success");
        updateAuthContextInfo({
          ...authContextInfo,
          auth: { status: AUTH_STATUS.AUTHENTICATED }
        });
      } else if (code === API_RESPONSE_CODE.OK && onlyVerificationRequired) {
        updateAuthContextInfo({
          ...authContextInfo,
          auth: { status: AUTH_STATUS.VERIFY_EMAIL_REQUIRED }
        });
      } else if (code === API_RESPONSE_CODE.OK && onlyPasswordChangeRequired) {
        updateAuthContextInfo({
          ...authContextInfo,
          auth: { status: AUTH_STATUS.CHANGE_PASSWORD_REQUIRED }
        });
      } else if (
        code === API_RESPONSE_CODE.OK &&
        verificationAndPasswordChangeRequired
      ) {
        updateAuthContextInfo({
          ...authContextInfo,
          auth: {
            status: AUTH_STATUS.EMAIL_VERIFICATION_AND_PASSWORD_CHANGE_REQUIRED
          }
        });
      } else {
        // ⚠️ Expected API/business failure — login rejected
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Login rejected by API",
          buildLogContext(
            "auth",
            "login",
            // userId/companyId/storeId not available — login did not succeed
            undefined,
            undefined,
            undefined,
            { apiCode: String(code) }
          )
        );
        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as ALERT_BANNER_CODE
        });
      }
    },

    onError: () => {
      // ❌ Unexpected runtime/network error
      // userId/companyId/storeId are not available — user never authenticated
      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: ALERT_BANNER_CODE.INTERNAL_ERROR
      });
    }
  });
};

export default useLogin;
